import { SLAB_BOUNDS } from '../../../game/phaserFacility/facilityLayout';
import { TILE_SIZE } from './facilityCoords';

// ── Build constants (world units) ───────────────────────────────────────────
export const FOUNDATION_HEIGHT = 0.12;
export const ROOF_THICKNESS = 0.12;
export const PILLAR_INSET = 0.12;

/**
 * Four corner-pillar offsets relative to a building's footprint center.
 * @param {number} boxW - footprint width (x)
 * @param {number} boxD - footprint depth (z)
 * @param {number} [inset=PILLAR_INSET] - distance pulled in from each corner
 * @returns {Array<[number, number]>} [dx, dz] offsets: [-,-],[+,-],[-,+],[+,+]
 */
export function cornerPillarOffsets(boxW, boxD, inset = PILLAR_INSET) {
  const hx = boxW / 2 - inset;
  const hz = boxD / 2 - inset;
  return [
    [-hx, -hz],
    [hx, -hz],
    [-hx, hz],
    [hx, hz],
  ];
}

/**
 * Evenly spaced offsets along a wall of given length, centered on 0.
 * @param {number} wallLength - length of the wall (x extent)
 * @param {number} count - number of windows
 * @param {number} [margin=0.3] - clear space kept at each wall end
 * @returns {number[]} offsets along the wall
 */
export function windowOffsets(wallLength, count, margin = 0.3) {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const usable = wallLength - margin * 2;
  if (usable <= 0) return [0];
  const step = usable / (count - 1);
  const start = -usable / 2;
  return Array.from({ length: count }, (_, i) => start + step * i);
}

/**
 * Chain-link fence geometry around a facility footprint, in world units.
 * @param {{col:number,row:number,w:number,h:number}} bounds - SLAB_BOUNDS shape
 * @param {object} [opts]
 * @param {number} [opts.tile] - tile size (defaults to TILE_SIZE)
 * @param {number} [opts.margin=1] - distance the fence sits beyond the footprint
 * @param {number} [opts.postSpacing=2] - target spacing between posts
 * @param {number} [opts.gateWidth=3] - width of the gate opening on the south edge
 * @returns {{posts:Array<[number,number]>, panels:Array<{cx:number,cz:number,length:number,rotationY:number}>, gate:{centerX:number,z:number,width:number}, bounds:{minX:number,minZ:number,maxX:number,maxZ:number}}}
 */
export function fenceLayout(bounds, opts = {}) {
  const tile = opts.tile ?? TILE_SIZE;
  const margin = opts.margin ?? 1;
  const postSpacing = opts.postSpacing ?? 2;
  const gateWidth = opts.gateWidth ?? 3;

  const minX = bounds.col * tile - margin;
  const minZ = bounds.row * tile - margin;
  const maxX = (bounds.col + bounds.w) * tile + margin;
  const maxZ = (bounds.row + bounds.h) * tile + margin;

  const gate = { centerX: (minX + maxX) / 2, z: maxZ, width: gateWidth };

  const edges = [
    { from: [minX, minZ], to: [maxX, minZ], axis: 'x' },        // north
    { from: [maxX, minZ], to: [maxX, maxZ], axis: 'z' },        // east
    { from: [minX, maxZ], to: [maxX, maxZ], axis: 'x', gate },  // south (gate)
    { from: [minX, minZ], to: [minX, maxZ], axis: 'z' },        // west
  ];

  const posts = [];
  const panels = [];

  edges.forEach(edge => {
    const [x0, z0] = edge.from;
    const [x1, z1] = edge.to;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    const segCount = Math.max(1, Math.round(len / postSpacing));
    const segLen = len / segCount;
    const ux = dx / len;
    const uz = dz / len;

    for (let i = 0; i <= segCount; i++) {
      posts.push([x0 + ux * segLen * i, z0 + uz * segLen * i]);
    }

    for (let i = 0; i < segCount; i++) {
      const cx = x0 + ux * segLen * (i + 0.5);
      const cz = z0 + uz * segLen * (i + 0.5);
      if (edge.gate && Math.abs(cx - edge.gate.centerX) < edge.gate.width / 2) {
        continue; // gate opening: no panel
      }
      panels.push({ cx, cz, length: segLen, rotationY: edge.axis === 'x' ? 0 : Math.PI / 2 });
    }
  });

  const seen = new Set();
  const dedupedPosts = posts.filter(([px, pz]) => {
    const key = `${px},${pz}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return { posts: dedupedPosts, panels, gate, bounds: { minX, minZ, maxX, maxZ } };
}

/**
 * Front-center door point of a room (south edge facing the worker yard).
 * @param {{col:number,row:number,w:number,h:number}} roomDef
 * @returns {[number,number,number]} world [x, y, z]
 */
export function roomDoorPos(roomDef, tile = TILE_SIZE) {
  const x = (roomDef.col + roomDef.w / 2) * tile;
  const z = (roomDef.row + roomDef.h) * tile;
  return [x, 0.2, z];
}

// Cluster pattern for workers standing in the yard
const YARD_CLUSTER = [
  [0, 0], [0.4, 0], [-0.4, 0], [0.8, 0], [-0.8, 0],
  [1.2, 0], [-1.2, 0], [0, 0.4], [0.4, 0.4], [-0.4, 0.4],
];

/**
 * Stages a worker in the front-yard strip between the buildings and the south fence.
 * @param {number} index - worker index
 * @param {{col:number,row:number,w:number,h:number}} [bounds=SLAB_BOUNDS]
 * @param {number} [tile=TILE_SIZE]
 * @returns {[number, number, number]} world position
 */
export function workerYardPos(index, bounds = SLAB_BOUNDS, tile = TILE_SIZE) {
  const minX = bounds.col * tile;
  const maxX = (bounds.col + bounds.w) * tile;
  const maxZ = (bounds.row + bounds.h) * tile;
  const centerX = (minX + maxX) / 2;
  const offset = YARD_CLUSTER[index % YARD_CLUSTER.length];
  const rowShift = Math.floor(index / YARD_CLUSTER.length) * 0.5;
  return [centerX + offset[0], 0.2, maxZ + 0.5 + offset[1] + rowShift];
}
